import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

// material-ui
import {
    Button, DialogContent, DialogTitle, Grid, InputLabel, Dialog,
    OutlinedInput, Stack, Alert, DialogActions, FormControlLabel, Switch
} from '@mui/material'

// third party
import * as Yup from 'yup'
import { Formik } from 'formik'

// project import
import AnimateButton from 'components/@extended/AnimateButton'
import { FormFieldError } from 'components/Form'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { useSnackbar } from 'notistack'
import { EVENT_CATEGORY_ADD_INIT, EVENT_CATEGORY_UPDATE_SUCCESS } from 'models/events/category.event'
import ImageUpload from 'components/common/ImageUpload'
import { categoryCreate } from '../../models/category'

/*
 * Initiate category popup
 */
export const CategoryAddDialog = () => {
    const [open, setOpen] = useState(false)
    const eventbus = useEventBusContext()

    useEffect(() => {
        return eventbus.on(EVENT_CATEGORY_ADD_INIT, () => {
            setOpen(true)
        })
    }, [eventbus])

    return (
        <Dialog open={open} scroll={'body'} maxWidth="md" fullWidth={true} onClose={() => setOpen(false)}>
            {open && <CategoryAdd onClose={() => setOpen(false)} onSuccess={(data) => {
                setOpen(false)
                eventbus.fire(EVENT_CATEGORY_UPDATE_SUCCESS, data)
            }} />}
        </Dialog>
    )
}

//category creation form validation rules
const validation = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string(),
    is_active: Yup.boolean().required('Status is required'),
    image: Yup.mixed().nullable()
})

/*
 * category create form
 */
const CategoryAdd = ({ onClose, onSuccess }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [imageFile, setImageFile] = useState(null)
    const [imagePreview, setImagePreview] = useState(null)

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageRemove = () => {
        setImageFile(null);
        setImagePreview(null);
    };

    //Send data to server
    const onSubmit = async (values, { setErrors }) => {
        const data = {
            name: values.name,
            description: values.description || '',
            is_active: values.is_active ? 'active' : 'inactive',
            image: imageFile ? [imageFile] : null
        };

        await categoryCreate(data).then((res) => {
            onSuccess(res.data)
            enqueueSnackbar(res.message || 'Category created successfully', { variant: 'success', autoHideDuration: 1500 })
        }).catch((res) => {
            if (res.errors) {
                res.errors['submit'] = res.message
                setErrors(res.errors)
            } else {
                setErrors({ submit: res.message })
            }
            enqueueSnackbar(res.message || 'Failed to create category', { variant: 'error', autoHideDuration: 1500 })
        })
    }

    return (
        <Formik 
            initialValues={{ 
                name: '', 
                description: '', 
                is_active: true,
                image: null
            }} 
            validationSchema={validation} 
            onSubmit={onSubmit}
        >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue }) => {
                return (
                    <form noValidate onSubmit={handleSubmit}>
                        <DialogTitle>Add Category</DialogTitle>

                        <DialogContent dividers>
                            <Grid container spacing={3}>
                                {errors.submit && (
                                    <Grid item size={{ xs: 12 }}>
                                        <Alert severity="error">{errors.submit}</Alert>
                                    </Grid>
                                )}
                                
                                <Grid item size={{ xs: 12 }} fullWidth>
                                    <Stack spacing={1}>
                                        <InputLabel htmlFor="category-name">Name *</InputLabel>
                                        <OutlinedInput
                                            id="category-name"
                                            type="text"
                                            value={values.name}
                                            name="name"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            placeholder="Enter category name"
                                            fullWidth
                                            error={Boolean(touched.name && errors.name)}
                                        />
                                        <FormFieldError touched={touched.name} errors={errors.name} />
                                    </Stack>
                                </Grid>

                                <Grid item size={{ xs: 12 }} fullWidth>
                                    <Stack spacing={1}>
                                        <InputLabel htmlFor="category-description">Description</InputLabel>
                                        <OutlinedInput
                                            id="category-description"
                                            type="text"
                                            value={values.description}
                                            name="description"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            placeholder="Enter description"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            error={Boolean(touched.description && errors.description)}
                                        />
                                        <FormFieldError touched={touched.description} errors={errors.description} />
                                    </Stack>
                                </Grid>

                                <Grid item size={{ xs: 12 }} fullWidth>
                                    <Stack spacing={1}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.is_active}
                                                    onChange={(e) => setFieldValue('is_active', e.target.checked)}
                                                    name="is_active"
                                                    color="primary"
                                                />
                                            }
                                            label="Is Active"
                                        />
                                        <FormFieldError touched={touched.is_active} errors={errors.is_active} />
                                    </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                    <Stack spacing={1}>
                                        <InputLabel>Image</InputLabel>
                                        <ImageUpload
                                            imagePreview={imagePreview}
                                            onImageChange={handleImageChange}
                                            onImageRemove={handleImageRemove}
                                            id="category-image-upload"
                                        />
                                        <FormFieldError touched={touched.image} errors={errors.image} />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogContent>

                        <DialogActions sx={{ p: 1.5 }}>
                            <AnimateButton>
                                <Button
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        onClose()
                                    }}
                                    type="button"
                                    variant="outlined"
                                    color="secondary"
                                >
                                    Cancel
                                </Button>
                            </AnimateButton>
                            <AnimateButton>
                                <Button disabled={isSubmitting} type="submit" variant="contained" color="primary">
                                    Create
                                </Button>
                            </AnimateButton>
                        </DialogActions>
                    </form>
                )
            }}
        </Formik>
    )
}

//category component prop type declaration
CategoryAdd.propTypes = {
    onClose: PropTypes.func,
    onSuccess: PropTypes.func
}

export default CategoryAdd

